(() => {
  if (window.inkhavenGridInserts) return;
  const sectionSelector = '[data-search-page]';
  const generatedSelector = '[data-grid-generated-insert]';
  const controllers = new WeakMap();
  let instance = 0;

  const seededRandom = (seed) => {
    let state = seed || 1;
    return () => {
      state ^= state << 13;
      state ^= state >>> 17;
      state ^= state << 5;
      return (state >>> 0) / 4294967296;
    };
  };

  // Choose the configured insert first, then its book; a large book pool must
  // not give shelf talkers more weight than another configured insert.
  const planInserts = (rows, candidates, random, state = {}) => {
    const usedBooks = new Set(state.usedBooks || []);
    let relatedUsed = !!state.relatedUsed;
    const plan = [];
    let chance = 25;
    for (let row = 1; row < rows.length - 1; row += 1) {
      const roll = random() * 100;
      if (roll >= chance) {
        chance = Math.min(100, chance + 10);
        continue;
      }
      const available = candidates.filter((candidate) => {
        if (candidate.kind === 'related-shelves') return !relatedUsed;
        if (candidate.kind === 'shelf-talker') return candidate.books.some((book) => !usedBooks.has(book));
        return true;
      });
      if (!available.length) {
        chance = Math.min(100, chance + 10);
        continue;
      }
      const candidate = available[Math.floor(random() * available.length)];
      let book = null;
      if (candidate.kind === 'shelf-talker') {
        const books = candidate.books.filter((id) => !usedBooks.has(id));
        book = books[Math.floor(random() * books.length)];
        usedBooks.add(book);
      }
      if (candidate.kind === 'related-shelves') relatedUsed = true;
      plan.push({ row, candidate, book, chance });
      chance = Math.max(0, chance - 20);
    }
    return plan;
  };

  const uniqueIds = (root, prefix) => {
    const elements = [root, ...root.querySelectorAll('*')];
    const ids = new Map();
    elements.forEach((element) => {
      if (!element.id) return;
      ids.set(element.id, `${prefix}-${element.id}`);
      element.id = ids.get(element.id);
    });
    elements.forEach((element) => {
      ['for', 'list', 'aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns'].forEach((attr) => {
        if (element.hasAttribute(attr)) element.setAttribute(attr, element.getAttribute(attr).split(' ').map((id) => ids.get(id) || id).join(' '));
      });
      const href = element.getAttribute('href');
      if (href?.startsWith('#') && ids.has(href.slice(1))) element.setAttribute('href', `#${ids.get(href.slice(1))}`);
    });
  };

  class GridInserts {
    constructor(section) {
      this.section = section;
      this.grid = section.querySelector('.search-page__grid');
      this.pool = section.querySelector('[data-grid-insert-pool]');
      this.seed = Math.floor(Math.random() * 4294967295) + 1;
      this.id = ++instance;
      this.columns = null;
      this.frame = null;
      if (!this.grid || !this.pool) return;
      this.candidates = [...this.pool.children].filter((element) => !element.matches('script, style, link, template')).map((element, id) => {
        const typed = element.matches('[data-grid-insert-kind]') ? element : element.querySelector('[data-grid-insert-kind]');
        const kind = typed?.dataset.gridInsertKind || 'configured';
        const variants = [...element.querySelectorAll('[data-grid-insert-variant]')];
        return { id, kind, element, variants, books: [...new Set(variants.map((item) => item.dataset.gridInsertBook))] };
      }).filter((candidate) => {
        if (candidate.kind === 'shelf-talker') return candidate.books.length > 0;
        if (candidate.kind === 'related-shelves') return !!candidate.element.querySelector('[data-related-shelves]');
        return !!candidate.element.innerHTML.trim();
      });
      this.onResize = () => {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => { this.frame = null; this.render(); });
      };
      this.onSelect = (event) => {
        if (!window.Shopify?.designMode) return;
        const selected = this.candidates.find((candidate) => [candidate.element, ...candidate.element.querySelectorAll('[data-shopify-editor-block]')].some((element) => {
          try {
            const block = JSON.parse(element.getAttribute('data-shopify-editor-block') || '{}');
            return block.id === event.detail?.blockId || block.domId === event.detail?.blockId;
          } catch { return false; }
        }));
        if (selected) this.render(selected);
      };
      this.onDeselect = () => { if (this.previewing) this.render(null, true); };
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('shopify:block:select', this.onSelect);
      document.addEventListener('shopify:block:deselect', this.onDeselect);
      this.render();
    }

    clear() {
      this.grid.querySelectorAll(`:scope > ${generatedSelector}`).forEach((element) => {
        element.dispatchEvent(new CustomEvent('carousel:content-removed', { bubbles: true }));
        element.remove();
      });
    }

    render(selected = null, force = false) {
      const columns = getComputedStyle(this.grid).gridTemplateColumns;
      // Compare the number of tracks: resizing within a breakpoint doesn't reroll.
      const count = columns.split(/\s+/).filter(Boolean).length;
      if (!selected && !force && this.columns === count) return;
      this.columns = count;
      this.previewing = !!selected;
      this.clear();
      const cards = [...this.grid.children].filter((element) => element.matches('.search-product-card, .search-result-card'));
      const rows = [];
      cards.forEach((card) => {
        const top = card.getBoundingClientRect().top;
        const previous = rows[rows.length - 1];
        if (!previous || Math.abs(previous.top - top) > 2) rows.push({ top, last: card });
        else previous.last = card;
      });
      // Respect inserts in other grids and a merchant-placed standalone rail.
      const relatedUsed = [...document.querySelectorAll('[data-related-shelves]')].some((element) => !element.closest('[data-grid-insert-owner]'));
      const usedBooks = [...document.querySelectorAll(`${generatedSelector}[data-grid-insert-book]`)].map((element) => element.dataset.gridInsertBook);
      const plan = selected && rows.length ? [{ row: Math.min(1, rows.length - 1), candidate: selected, book: selected.books[0] || null }] : planInserts(rows, this.candidates, seededRandom(this.seed), { relatedUsed, usedBooks });
      plan.forEach(({ row, candidate, book, chance }, index) => {
        const insert = document.createElement('div');
        insert.className = 'search-page__grid-insert';
        insert.dataset.gridGeneratedInsert = String(candidate.id);
        insert.dataset.gridInsertKind = candidate.kind;
        insert.dataset.gridInsertAfterRow = String(row + 1);
        if (chance !== undefined) insert.dataset.gridInsertChance = String(chance);
        if (book) insert.dataset.gridInsertBook = book;
        const clone = candidate.element.cloneNode(true);
        if (book) {
          const variant = [...clone.querySelectorAll('[data-grid-insert-variant]')].find((item) => item.dataset.gridInsertBook === book);
          const holder = clone.matches('[data-grid-insert-kind]') ? clone : clone.querySelector('[data-grid-insert-kind]');
          holder.replaceChildren(variant.content.cloneNode(true));
        }
        uniqueIds(clone, `grid-insert-${this.id}-${index}`);
        insert.append(clone);
        rows[row].last.after(insert);
        insert.dispatchEvent(new CustomEvent('carousel:content-updated', { bubbles: true }));
      });
    }

    destroy() {
      if (!this.grid || !this.pool) return;
      this.clear();
      if (this.frame) cancelAnimationFrame(this.frame);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onSelect);
      document.removeEventListener('shopify:block:deselect', this.onDeselect);
    }
  }

  const init = (scope = document) => {
    const sections = [...scope.querySelectorAll(sectionSelector)];
    if (scope.matches?.(sectionSelector)) sections.unshift(scope);
    sections.forEach((section) => {
      controllers.get(section)?.destroy();
      controllers.set(section, new GridInserts(section));
    });
  };
  window.inkhavenGridInserts = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const sections = [...event.target.querySelectorAll(sectionSelector)];
    if (event.target.matches?.(sectionSelector)) sections.push(event.target);
    sections.forEach((section) => controllers.get(section)?.destroy());
  });
})();
